// =============================================================
// AIConfigurationPage — Super Admin Portal
// Features:
// 1. Dynamic AI Model Switch & Runtime Key Pool Management.
// 2. Multi-Key Pool for free accounts (Auto-failover on 429 quota exhaustion).
// 3. Manual Key Switching & Independent Key Connectivity Testing.
// 4. Strict isolation: NO auto-saving when changing models/providers.
//    Changes only apply when clicking "Save & Apply Config".
// =============================================================

import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, AIConfigData, AIPoolItem, TestAIConfigResult } from '../lib/api';
import { IconGemini, IconSettings, IconCheck, IconClose } from '../components/icons';

const GEMINI_MODELS = [
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', badge: 'Latest & Recommended' },
  { id: 'gemini-3.5-flash-lite', name: 'Gemini 3.5 Flash Lite', badge: 'Experimental Ultra-Fast' },
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', badge: 'Next-Gen Flash' },
  { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', badge: 'Fast & Lightweight Free Tier' },
  { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', badge: 'High Reasoning' },
];

const OPENAI_MODELS = [
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', badge: 'Fast & Affordable' },
  { id: 'gpt-4o', name: 'GPT-4o (Omni)', badge: 'Most Capable' },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', badge: 'High Performance' },
];

export default function AIConfigurationPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingKeyId, setTestingKeyId] = useState<string | null>(null);
  const [config, setConfig] = useState<AIConfigData | null>(null);

  // Form / Draft State (NOT saved until clicking "Save & Apply Config")
  const [selectedProvider, setSelectedProvider] = useState<'gemini' | 'openai' | 'mock'>('gemini');
  const [geminiModel, setGeminiModel] = useState('gemini-2.5-flash');
  const [customGeminiModel, setCustomGeminiModel] = useState('');
  const [openaiModel, setOpenaiModel] = useState('gpt-4o-mini');
  const [customOpenaiModel, setCustomOpenaiModel] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(120);

  // Pool Add Form State
  const [showAddGeminiKey, setShowAddGeminiKey] = useState(false);
  const [geminiNewKey, setGeminiNewKey] = useState('');
  const [geminiNewLabel, setGeminiNewLabel] = useState('');
  const [addingGeminiKey, setAddingGeminiKey] = useState(false);

  const [showAddOpenaiKey, setShowAddOpenaiKey] = useState(false);
  const [openaiNewKey, setOpenaiNewKey] = useState('');
  const [openaiNewLabel, setOpenaiNewLabel] = useState('');
  const [addingOpenaiKey, setAddingOpenaiKey] = useState(false);

  // Action status per key
  const [activatingKeyId, setActivatingKeyId] = useState<string | null>(null);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  // Feedback Messages
  const [bannerSuccess, setBannerSuccess] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestAIConfigResult | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  // Cooldown countdown timer tick
  useEffect(() => {
    const timer = setInterval(() => {
      setConfig((prev) => {
        if (!prev) return prev;
        let changed = false;

        const updatePool = (pool: AIPoolItem[]) =>
          pool.map((k) => {
            if (k.cooldownSecondsRemaining && k.cooldownSecondsRemaining > 0) {
              changed = true;
              const nextVal = k.cooldownSecondsRemaining - 1;
              return {
                ...k,
                cooldownSecondsRemaining: nextVal > 0 ? nextVal : undefined,
                status: nextVal <= 0 && k.status === 'RATE_LIMITED' ? ('ACTIVE' as const) : k.status,
              };
            }
            return k;
          });

        const newGemini = updatePool(prev.geminiPool || []);
        const newOpenai = updatePool(prev.openaiPool || []);

        if (!changed) return prev;
        return { ...prev, geminiPool: newGemini, openaiPool: newOpenai };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  async function fetchConfig() {
    setLoading(true);
    try {
      const data = await adminApi.getAIConfig();
      setConfig(data);
      applyConfigToForm(data);
    } catch (err: any) {
      setBannerError(err.message || 'Failed to load AI configuration.');
    } finally {
      setLoading(false);
    }
  }

  function applyConfigToForm(data: AIConfigData) {
    setSelectedProvider(data.provider);

    // Gemini Model resolution
    if (GEMINI_MODELS.some((m) => m.id === data.geminiModel)) {
      setGeminiModel(data.geminiModel);
      setCustomGeminiModel('');
    } else {
      setGeminiModel('custom');
      setCustomGeminiModel(data.geminiModel);
    }

    // OpenAI Model resolution
    if (OPENAI_MODELS.some((m) => m.id === data.openaiModel)) {
      setOpenaiModel(data.openaiModel);
      setCustomOpenaiModel('');
    } else {
      setOpenaiModel('custom');
      setCustomOpenaiModel(data.openaiModel);
    }

    setTimeoutSeconds(Math.round(data.timeoutMs / 1000));
  }

  // Check if current form draft has unsaved differences vs saved backend config
  const activeGeminiModelDraft = geminiModel === 'custom' ? customGeminiModel.trim() : geminiModel;
  const activeOpenaiModelDraft = openaiModel === 'custom' ? customOpenaiModel.trim() : openaiModel;

  const hasUnsavedChanges = Boolean(
    config &&
      (selectedProvider !== config.provider ||
        activeGeminiModelDraft !== config.geminiModel ||
        activeOpenaiModelDraft !== config.openaiModel ||
        timeoutSeconds !== Math.round(config.timeoutMs / 1000))
  );

  function handleDiscardChanges() {
    if (config) {
      applyConfigToForm(config);
      setBannerSuccess('Unsaved changes discarded. Form reset to active configuration.');
      setTimeout(() => setBannerSuccess(null), 3500);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setBannerSuccess(null);
    setBannerError(null);

    try {
      const payload: any = {
        provider: selectedProvider,
        geminiModel: activeGeminiModelDraft,
        openaiModel: activeOpenaiModelDraft,
        timeoutMs: timeoutSeconds * 1000,
      };

      const updated = await adminApi.saveAIConfig(payload);
      setConfig(updated);
      setBannerSuccess(
        `AI Configuration successfully saved & applied! Live AI features are now using ${selectedProvider.toUpperCase()} (${
          selectedProvider === 'gemini'
            ? activeGeminiModelDraft
            : selectedProvider === 'openai'
            ? activeOpenaiModelDraft
            : 'Mock Mode'
        }).`
      );
    } catch (err: any) {
      setBannerError(err.message || 'Failed to save AI configuration.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSetActiveKey(provider: 'gemini' | 'openai', keyId: string) {
    setActivatingKeyId(keyId);
    setBannerError(null);
    setBannerSuccess(null);
    try {
      const updated = await adminApi.setActiveKeyInPool(provider, keyId);
      setConfig(updated);
      const activeItem = (provider === 'gemini' ? updated.geminiPool : updated.openaiPool).find((k) => k.id === keyId);
      setBannerSuccess(`Successfully switched active key to "${activeItem?.label || keyId}".`);
    } catch (err: any) {
      setBannerError(err.message || 'Failed to switch active key.');
    } finally {
      setActivatingKeyId(null);
    }
  }

  async function handleAddKey(provider: 'gemini' | 'openai') {
    const rawKey = provider === 'gemini' ? geminiNewKey.trim() : openaiNewKey.trim();
    const label = provider === 'gemini' ? geminiNewLabel.trim() : openaiNewLabel.trim();

    if (!rawKey) {
      setBannerError('API key value is required.');
      return;
    }

    if (provider === 'gemini') setAddingGeminiKey(true);
    else setAddingOpenaiKey(true);

    setBannerError(null);
    setBannerSuccess(null);

    try {
      const updated = await adminApi.addKeyToPool(provider, rawKey, label || undefined);
      setConfig(updated);
      if (provider === 'gemini') {
        setGeminiNewKey('');
        setGeminiNewLabel('');
        setShowAddGeminiKey(false);
      } else {
        setOpenaiNewKey('');
        setOpenaiNewLabel('');
        setShowAddOpenaiKey(false);
      }
      setBannerSuccess(`Key added to ${provider.toUpperCase()} pool successfully! Auto-failover will utilize this key if others reach quota.`);
    } catch (err: any) {
      setBannerError(err.message || 'Failed to add key to pool.');
    } finally {
      if (provider === 'gemini') setAddingGeminiKey(false);
      else setAddingOpenaiKey(false);
    }
  }

  async function handleDeleteKey(provider: 'gemini' | 'openai', keyId: string, label: string) {
    if (!window.confirm(`Are you sure you want to remove "${label}" from the key pool?`)) {
      return;
    }

    setDeletingKeyId(keyId);
    setBannerError(null);
    setBannerSuccess(null);

    try {
      const updated = await adminApi.removeKeyFromPool(provider, keyId);
      setConfig(updated);
      setBannerSuccess(`Key "${label}" removed from ${provider.toUpperCase()} pool.`);
    } catch (err: any) {
      setBannerError(err.message || 'Failed to remove key from pool.');
    } finally {
      setDeletingKeyId(null);
    }
  }

  async function handleTestKey(provider: 'gemini' | 'openai', keyId?: string) {
    setTesting(true);
    setTestingKeyId(keyId || 'general');
    setTestResult(null);
    setBannerError(null);

    const activeModel = provider === 'gemini' ? activeGeminiModelDraft : activeOpenaiModelDraft;

    try {
      const res = await adminApi.testAIConfig({
        provider,
        model: activeModel,
        keyId,
      });
      setTestResult(res);
    } catch (err: any) {
      setTestResult({
        success: false,
        latencyMs: 0,
        message: err.message || 'Test request failed.',
      });
    } finally {
      setTesting(false);
      setTestingKeyId(null);
    }
  }

  if (!user?.isSuperAdmin) {
    return (
      <DashboardLayout title="Access Denied" subtitle="Super Admin access required">
        <div style={{ maxWidth: 800, margin: '40px auto', padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🔒</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' }}>Super Admin Access Required</h2>
          <p style={{ color: '#64748b', marginTop: 8 }}>
            AI Engine configuration, runtime model switching, and API key pools can only be managed by platform Super Administrators.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="AI Configuration" subtitle="Super Admin Dynamic Model Switching & API Key Pool">
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '24px 16px' }}>
        {/* Breadcrumb & Live System Status */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#64748b' }}>
            <span>Super Admin</span>
            <span>/</span>
            <span style={{ color: '#0f172a', fontWeight: 500 }}>AI Engine & Key Pool</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
                AI Model Switch & Free Key Pool Failover
              </h1>
              <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>
                Switch models dynamically and pool multiple free-tier keys with automatic failover on 429 quota limits.
              </p>
            </div>

            {config && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Saved Live Provider Badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Live Active:</span>
                  <span
                    style={{
                      padding: '4px 12px',
                      borderRadius: 9999,
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      backgroundColor: config.provider === 'gemini' ? '#eff6ff' : config.provider === 'openai' ? '#f0fdf4' : '#f1f5f9',
                      color: config.provider === 'gemini' ? '#1d4ed8' : config.provider === 'openai' ? '#15803d' : '#475569',
                      border: `1px solid ${config.provider === 'gemini' ? '#bfdbfe' : config.provider === 'openai' ? '#bbf7d0' : '#cbd5e1'}`,
                    }}
                  >
                    ● {config.provider} ({config.provider === 'gemini' ? config.geminiModel : config.provider === 'openai' ? config.openaiModel : 'offline'})
                  </span>
                </div>

                {/* Unsaved Draft Indicator Badge */}
                {hasUnsavedChanges && (
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: 9999,
                      fontSize: '0.76rem',
                      fontWeight: 700,
                      backgroundColor: '#fffbeb',
                      color: '#b45309',
                      border: '1px solid #fde68a',
                    }}
                  >
                    ● Unsaved Draft
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Unsaved Changes Banner */}
        {hasUnsavedChanges && (
          <div
            style={{
              padding: '14px 18px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 10,
              color: '#92400e',
              fontSize: '0.88rem',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              <div>
                <strong>Unsaved Changes in Draft:</strong> You have changed provider or model options.
                <span style={{ display: 'block', fontSize: '0.8rem', color: '#b45309', marginTop: 2 }}>
                  Nothing changes in the live system until you click <strong>"Save & Apply Config"</strong> below.
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleDiscardChanges}
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
              >
                Discard Changes
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary btn-sm"
                style={{ fontSize: '0.8rem', padding: '6px 14px' }}
              >
                {saving ? 'Saving...' : 'Save & Apply Config'}
              </button>
            </div>
          </div>
        )}

        {/* Feedback Banners */}
        {bannerSuccess && (
          <div
            style={{
              padding: '14px 16px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              color: '#15803d',
              fontSize: '0.88rem',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ fontWeight: 700 }}>✓</span>
            <span>{bannerSuccess}</span>
          </div>
        )}

        {bannerError && (
          <div
            style={{
              padding: '14px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: '0.88rem',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span>⚠</span>
            <span>{bannerError}</span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>Loading AI system configurations & key pools...</div>
        ) : (
          <form onSubmit={handleSave}>
            {/* Step 1: Select AI Provider */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: 24,
                marginBottom: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                  1. Select AI Provider
                </h2>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  Click to select draft provider (requires clicking Save to apply)
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                {/* Google Gemini Card */}
                <div
                  onClick={() => setSelectedProvider('gemini')}
                  style={{
                    padding: 16,
                    borderRadius: 10,
                    border: `2px solid ${selectedProvider === 'gemini' ? '#2563eb' : '#e2e8f0'}`,
                    backgroundColor: selectedProvider === 'gemini' ? '#f8fafc' : '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          backgroundColor: '#eff6ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconGemini style={{ width: 20, height: 20, color: '#2563eb' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>Google Gemini</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Free tier & multi-key pool failover</div>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="provider"
                      checked={selectedProvider === 'gemini'}
                      onChange={() => setSelectedProvider('gemini')}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#475569' }}>
                    Supports 2.5 Flash, 3.5 Flash Lite, 2.0 Flash, and Pro. Multi-key pool auto-failover supported.
                  </div>
                  <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>Pool: {config?.geminiPool?.length || 0} Key(s) Configured</span>
                    {config?.provider === 'gemini' && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 4 }}>
                        Live Active
                      </span>
                    )}
                  </div>
                </div>

                {/* OpenAI Card */}
                <div
                  onClick={() => setSelectedProvider('openai')}
                  style={{
                    padding: 16,
                    borderRadius: 10,
                    border: `2px solid ${selectedProvider === 'openai' ? '#2563eb' : '#e2e8f0'}`,
                    backgroundColor: selectedProvider === 'openai' ? '#f8fafc' : '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          backgroundColor: '#f0fdf4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span style={{ fontSize: '1.2rem' }}>⚡</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>OpenAI</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>GPT-4o Mini / GPT-4o with key pool</div>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="provider"
                      checked={selectedProvider === 'openai'}
                      onChange={() => setSelectedProvider('openai')}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#475569' }}>
                    Supports GPT-4o, GPT-4o Mini, and GPT-4 Turbo. Multiple keys fail over on 429 quota limits.
                  </div>
                  <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>Pool: {config?.openaiPool?.length || 0} Key(s) Configured</span>
                    {config?.provider === 'openai' && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 4 }}>
                        Live Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Mock / Offline Card */}
                <div
                  onClick={() => setSelectedProvider('mock')}
                  style={{
                    padding: 16,
                    borderRadius: 10,
                    border: `2px solid ${selectedProvider === 'mock' ? '#2563eb' : '#e2e8f0'}`,
                    backgroundColor: selectedProvider === 'mock' ? '#f8fafc' : '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          backgroundColor: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconSettings style={{ width: 18, height: 18, color: '#475569' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>Mock / Offline</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Deterministic test mode</div>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="provider"
                      checked={selectedProvider === 'mock'}
                      onChange={() => setSelectedProvider('mock')}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#475569' }}>
                    Uses deterministic heuristic rules without calling external APIs (useful for zero-cost offline testing).
                  </div>
                  <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#64748b' }}>
                    No API keys required.
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Google Gemini Model & Key Pool Manager */}
            {selectedProvider === 'gemini' && (
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 24,
                  marginBottom: 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div>
                    <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                      2. Google Gemini Parameters & Key Pool
                    </h2>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.83rem' }}>
                      Configure model family and manage multiple free-tier keys with automated 429 quota failover.
                    </p>
                  </div>
                </div>

                {/* Model Selector */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                    Select Gemini Model:
                  </label>
                  <select
                    value={geminiModel}
                    onChange={(e) => setGeminiModel(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: 420,
                      padding: '9px 12px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      color: '#0f172a',
                    }}
                  >
                    {GEMINI_MODELS.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.badge})
                      </option>
                    ))}
                    <option value="custom">Custom Model Name...</option>
                  </select>

                  {geminiModel === 'custom' && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        type="text"
                        placeholder="Enter custom Gemini model identifier (e.g. gemini-2.0-flash)"
                        value={customGeminiModel}
                        onChange={(e) => setCustomGeminiModel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.preventDefault();
                        }}
                        style={{
                          width: '100%',
                          maxWidth: 420,
                          padding: '9px 12px',
                          borderRadius: 6,
                          border: '1px solid #cbd5e1',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Gemini Key Pool Section */}
                <div
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: 18,
                    marginTop: 16,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.92rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>Gemini Multi-Key Pool</span>
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 9999 }}>
                          Auto Failover Enabled
                        </span>
                      </div>
                      <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                        Add multiple free Gemini accounts. If key 1 hits daily/minute rate limits, requests automatically switch to key 2.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowAddGeminiKey(!showAddGeminiKey)}
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {showAddGeminiKey ? 'Cancel' : '+ Add Key to Pool'}
                    </button>
                  </div>

                  {/* Add Key Form */}
                  {showAddGeminiKey && (
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: 8,
                        padding: 14,
                        marginBottom: 16,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#0f172a', marginBottom: 8 }}>
                        Add Gemini Free API Key to Pool:
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: '1 1 200px' }}>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>
                            Key Label / Account Name:
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Work Account, Backup #2"
                            value={geminiNewLabel}
                            onChange={(e) => setGeminiNewLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: '0.85rem',
                            }}
                          />
                        </div>
                        <div style={{ flex: '2 1 280px' }}>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>
                            Gemini API Key (AIzaSy...):
                          </label>
                          <input
                            type="password"
                            placeholder="Paste Google AI Studio API Key"
                            value={geminiNewKey}
                            onChange={(e) => setGeminiNewKey(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: '0.85rem',
                              fontFamily: 'monospace',
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddKey('gemini')}
                          disabled={addingGeminiKey || !geminiNewKey.trim()}
                          className="btn btn-primary btn-sm"
                          style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                        >
                          {addingGeminiKey ? 'Adding...' : 'Add to Pool'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Key Pool Table */}
                  {config?.geminiPool && config.geminiPool.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {config.geminiPool.map((item, idx) => (
                        <div
                          key={item.id}
                          style={{
                            backgroundColor: '#ffffff',
                            border: `1px solid ${item.isActive ? '#93c5fd' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 10,
                            boxShadow: item.isActive ? '0 1px 3px rgba(37, 99, 235, 0.08)' : 'none',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: item.isActive ? '#eff6ff' : '#f1f5f9',
                                color: item.isActive ? '#2563eb' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
                                  {item.label}
                                </span>
                                {item.isActive && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 9999,
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      backgroundColor: '#dcfce7',
                                      color: '#15803d',
                                      border: '1px solid #bbf7d0',
                                    }}
                                  >
                                    Active Primary
                                  </span>
                                )}
                                {item.status === 'RATE_LIMITED' && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 9999,
                                      fontSize: '0.72rem',
                                      fontWeight: 600,
                                      backgroundColor: '#fef3c7',
                                      color: '#b45309',
                                      border: '1px solid #fde68a',
                                    }}
                                  >
                                    Cooldown ({item.cooldownSecondsRemaining || 60}s)
                                  </span>
                                )}
                                {!item.isActive && item.status !== 'RATE_LIMITED' && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 9999,
                                      fontSize: '0.72rem',
                                      backgroundColor: '#f1f5f9',
                                      color: '#475569',
                                    }}
                                  >
                                    Standby
                                  </span>
                                )}
                              </div>
                              <div style={{ marginTop: 2, fontSize: '0.8rem', color: '#64748b', fontFamily: 'monospace' }}>
                                Key: {item.maskedKey}
                              </div>
                            </div>
                          </div>

                          {/* Key Actions: Manual Switch, Test, Remove */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {!item.isActive && (
                              <button
                                type="button"
                                onClick={() => handleSetActiveKey('gemini', item.id)}
                                disabled={activatingKeyId === item.id}
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                              >
                                {activatingKeyId === item.id ? 'Switching...' : 'Switch to This Key'}
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => handleTestKey('gemini', item.id)}
                              disabled={testing && testingKeyId === item.id}
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                            >
                              {testing && testingKeyId === item.id ? 'Testing...' : 'Test Key'}
                            </button>

                            {config.geminiPool.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleDeleteKey('gemini', item.id, item.label)}
                                disabled={deletingKeyId === item.id}
                                className="btn btn-secondary btn-sm"
                                style={{
                                  fontSize: '0.78rem',
                                  padding: '5px 10px',
                                  color: '#dc2626',
                                  borderColor: '#fecaca',
                                }}
                              >
                                {deletingKeyId === item.id ? '...' : 'Remove'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: 18, textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                      No Gemini keys in pool yet. Click <strong>"+ Add Key to Pool"</strong> to add your first Google AI key.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: OpenAI Model & Key Pool Manager */}
            {selectedProvider === 'openai' && (
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 24,
                  marginBottom: 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div>
                    <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                      2. OpenAI Parameters & Key Pool
                    </h2>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.83rem' }}>
                      Configure OpenAI model and manage multiple account keys with automatic failover on 429 quota limits.
                    </p>
                  </div>
                </div>

                {/* Model Selector */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                    Select OpenAI Model:
                  </label>
                  <select
                    value={openaiModel}
                    onChange={(e) => setOpenaiModel(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: 420,
                      padding: '9px 12px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      color: '#0f172a',
                    }}
                  >
                    {OPENAI_MODELS.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.badge})
                      </option>
                    ))}
                    <option value="custom">Custom Model Name...</option>
                  </select>

                  {openaiModel === 'custom' && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        type="text"
                        placeholder="Enter custom OpenAI model identifier (e.g. gpt-4o-2024-08-06)"
                        value={customOpenaiModel}
                        onChange={(e) => setCustomOpenaiModel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.preventDefault();
                        }}
                        style={{
                          width: '100%',
                          maxWidth: 420,
                          padding: '9px 12px',
                          borderRadius: 6,
                          border: '1px solid #cbd5e1',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* OpenAI Key Pool Section */}
                <div
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: 18,
                    marginTop: 16,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.92rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>OpenAI Multi-Key Pool</span>
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 9999 }}>
                          Auto Failover Enabled
                        </span>
                      </div>
                      <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                        Pool multiple OpenAI keys. If one key reaches quota or rate limits, requests fail over to the next standby key automatically.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowAddOpenaiKey(!showAddOpenaiKey)}
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {showAddOpenaiKey ? 'Cancel' : '+ Add Key to Pool'}
                    </button>
                  </div>

                  {/* Add OpenAI Key Form */}
                  {showAddOpenaiKey && (
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: 8,
                        padding: 14,
                        marginBottom: 16,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#0f172a', marginBottom: 8 }}>
                        Add OpenAI API Key to Pool:
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: '1 1 200px' }}>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>
                            Key Label / Account Name:
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Primary Org, Project 2 Key"
                            value={openaiNewLabel}
                            onChange={(e) => setOpenaiNewLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: '0.85rem',
                            }}
                          />
                        </div>
                        <div style={{ flex: '2 1 280px' }}>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>
                            OpenAI API Key (sk-...):
                          </label>
                          <input
                            type="password"
                            placeholder="Paste OpenAI API Key (sk-proj-...)"
                            value={openaiNewKey}
                            onChange={(e) => setOpenaiNewKey(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: '0.85rem',
                              fontFamily: 'monospace',
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddKey('openai')}
                          disabled={addingOpenaiKey || !openaiNewKey.trim()}
                          className="btn btn-primary btn-sm"
                          style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                        >
                          {addingOpenaiKey ? 'Adding...' : 'Add to Pool'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* OpenAI Pool Table */}
                  {config?.openaiPool && config.openaiPool.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {config.openaiPool.map((item, idx) => (
                        <div
                          key={item.id}
                          style={{
                            backgroundColor: '#ffffff',
                            border: `1px solid ${item.isActive ? '#93c5fd' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 10,
                            boxShadow: item.isActive ? '0 1px 3px rgba(37, 99, 235, 0.08)' : 'none',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: item.isActive ? '#eff6ff' : '#f1f5f9',
                                color: item.isActive ? '#2563eb' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
                                  {item.label}
                                </span>
                                {item.isActive && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 9999,
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      backgroundColor: '#dcfce7',
                                      color: '#15803d',
                                      border: '1px solid #bbf7d0',
                                    }}
                                  >
                                    Active Primary
                                  </span>
                                )}
                                {item.status === 'RATE_LIMITED' && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 9999,
                                      fontSize: '0.72rem',
                                      fontWeight: 600,
                                      backgroundColor: '#fef3c7',
                                      color: '#b45309',
                                      border: '1px solid #fde68a',
                                    }}
                                  >
                                    Cooldown ({item.cooldownSecondsRemaining || 60}s)
                                  </span>
                                )}
                                {!item.isActive && item.status !== 'RATE_LIMITED' && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 9999,
                                      fontSize: '0.72rem',
                                      backgroundColor: '#f1f5f9',
                                      color: '#475569',
                                    }}
                                  >
                                    Standby
                                  </span>
                                )}
                              </div>
                              <div style={{ marginTop: 2, fontSize: '0.8rem', color: '#64748b', fontFamily: 'monospace' }}>
                                Key: {item.maskedKey}
                              </div>
                            </div>
                          </div>

                          {/* Key Actions */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {!item.isActive && (
                              <button
                                type="button"
                                onClick={() => handleSetActiveKey('openai', item.id)}
                                disabled={activatingKeyId === item.id}
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                              >
                                {activatingKeyId === item.id ? 'Switching...' : 'Switch to This Key'}
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => handleTestKey('openai', item.id)}
                              disabled={testing && testingKeyId === item.id}
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                            >
                              {testing && testingKeyId === item.id ? 'Testing...' : 'Test Key'}
                            </button>

                            {config.openaiPool.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleDeleteKey('openai', item.id, item.label)}
                                disabled={deletingKeyId === item.id}
                                className="btn btn-secondary btn-sm"
                                style={{
                                  fontSize: '0.78rem',
                                  padding: '5px 10px',
                                  color: '#dc2626',
                                  borderColor: '#fecaca',
                                }}
                              >
                                {deletingKeyId === item.id ? '...' : 'Remove'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: 18, textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                      No OpenAI keys in pool yet. Click <strong>"+ Add Key to Pool"</strong> to add an OpenAI key.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Request Timeouts & Limits */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: 24,
                marginBottom: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', margin: '0 0 16px 0' }}>
                3. Execution Timeouts
              </h2>
              <div style={{ maxWidth: 300 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Request Timeout (seconds):
                </label>
                <input
                  type="number"
                  min="15"
                  max="300"
                  value={timeoutSeconds}
                  onChange={(e) => setTimeoutSeconds(parseInt(e.target.value, 10) || 120)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                  }}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                  Default is 120s. Deep SEO audit rules and opportunity generation are allowed up to this duration.
                </p>
              </div>
            </div>

            {/* Test Connection Output */}
            {testResult && (
              <div
                style={{
                  padding: 16,
                  borderRadius: 10,
                  marginBottom: 20,
                  backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: testResult.success ? '#15803d' : '#b91c1c' }}>
                    {testResult.success ? '✓ Connection Verified' : '✕ Connection Test Failed'}
                  </div>
                  {testResult.latencyMs > 0 && (
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Latency: {testResult.latencyMs}ms</span>
                  )}
                </div>
                <div style={{ fontSize: '0.85rem', color: testResult.success ? '#166534' : '#991b1b' }}>
                  {testResult.message}
                </div>
                {testResult.sampleOutput && (
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#334155' }}>
                    <strong>Model Response:</strong> <code>{testResult.sampleOutput}</code>
                  </div>
                )}
              </div>
            )}

            {/* Action Bar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: 8,
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <button
                type="button"
                onClick={() => handleTestKey(selectedProvider === 'mock' ? 'gemini' : selectedProvider)}
                disabled={testing || saving || selectedProvider === 'mock'}
                className="btn btn-secondary"
                style={{ minWidth: 160 }}
              >
                {testing && testingKeyId === 'general' ? 'Testing...' : '⚡ Test Active Key'}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {hasUnsavedChanges && (
                  <button
                    type="button"
                    onClick={handleDiscardChanges}
                    disabled={saving}
                    className="btn btn-secondary"
                  >
                    Discard Changes
                  </button>
                )}

                <button
                  type="submit"
                  disabled={saving || testing}
                  className="btn btn-primary"
                  style={{
                    minWidth: 180,
                    fontWeight: 600,
                    boxShadow: hasUnsavedChanges ? '0 0 0 3px rgba(37, 99, 235, 0.25)' : undefined,
                  }}
                >
                  {saving ? 'Applying...' : 'Save & Apply Config'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
